import React, { useState, useRef, useEffect } from 'react';
import { db, auth, handleFirestoreError, OperationType } from '../lib/firebase';
import { collection, addDoc, query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { Calendar, Upload, Globe, AlertCircle, CheckCircle2, Loader2, Info, RefreshCcw, History, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { format, parseISO, isWithinInterval } from 'date-fns';

import { EVENT_TYPES } from '../constants';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';

export default function IcsImporter() {
  const [icsUrl, setIcsUrl] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [defaultType, setDefaultType] = useState('Away Mission');
  const [defaultPoints, setDefaultPoints] = useState(10);
  const [lastSync, setLastSync] = useState<{ 
    source: string, 
    timestamp: string, 
    count: number,
    startDate?: string,
    endDate?: string 
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync Range Modal State
  const [isRangeModalOpen, setIsRangeModalOpen] = useState(false);
  const [pendingContent, setPendingContent] = useState('');
  const [pendingSource, setPendingSource] = useState('');
  const [startDateSync, setStartDateSync] = useState(`${new Date().getFullYear()}-01-01`);
  const [endDateSync, setEndDateSync] = useState(`${new Date().getFullYear()}-12-31`);

  useEffect(() => {
    const fetchSyncInfo = async () => {
      const isCustomAuth = !!localStorage.getItem('lcars_custom_session');
      if (!auth.currentUser && !isCustomAuth) return; // Wait for auth
      try {
        const docSnap = await getDoc(doc(db, 'settings', 'calendarSyncInfo'));
        if (docSnap.exists()) {
          const data = docSnap.data() as any;
          setLastSync(data);
          if (data.source && data.source.startsWith('http')) {
            setIcsUrl(data.source);
          }
          const todayStr = new Date().toISOString().split('T')[0];
          setStartDateSync(todayStr);
        }
      } catch (err) {
        handleFirestoreError(err, OperationType.GET, 'settings/calendarSyncInfo');
      }
    };
    
    const isCustomAuth = !!localStorage.getItem('lcars_custom_session');
    if (isCustomAuth) {
      fetchSyncInfo();
    }

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchSyncInfo();
      }
    });
    
    return () => unsubscribe();
  }, []);

  const unescapeIcs = (str: string) => {
    if (!str) return '';
    return str.replace(/\\([,;\\nN])/g, (match, char) => {
      if (char.toLowerCase() === 'n') return '\n';
      return char;
    }).trim();
  };

  const parseIcs = (content: string) => {
    const events: any[] = [];
    const vevents = content.split('BEGIN:VEVENT');
    
    // Skip the preamble before the first VEVENT
    for (let i = 1; i < vevents.length; i++) {
      const vevent = vevents[i];
      const event: any = {};
      
      const lines = vevent.split(/\r?\n/);
      for (const line of lines) {
        if (line.startsWith('SUMMARY:')) event.name = unescapeIcs(line.replace('SUMMARY:', ''));
        if (line.startsWith('LOCATION:')) event.location = unescapeIcs(line.replace('LOCATION:', ''));
        if (line.startsWith('DESCRIPTION:')) event.description = unescapeIcs(line.replace('DESCRIPTION:', ''));
        if (line.startsWith('DTSTART')) {
          const parts = line.split(':');
          event.startDate = parseIcsDate(parts[1]?.trim());
        }
        if (line.startsWith('DTEND')) {
          const parts = line.split(':');
          event.endDate = parseIcsDate(parts[1]?.trim());
        }
      }
      
      if (event.name && event.startDate) {
        events.push(event);
      }
    }
    return events;
  };

  const parseIcsDate = (icsDate: string) => {
    if (!icsDate) return null;
    // Format: 20260522T100000Z or 20260522
    const year = icsDate.substring(0, 4);
    const month = icsDate.substring(4, 6);
    const day = icsDate.substring(6, 8);
    
    let time = '00:00:00';
    if (icsDate.includes('T')) {
      const hour = icsDate.substring(9, 11);
      const min = icsDate.substring(11, 13);
      const sec = icsDate.substring(13, 15);
      time = `${hour}:${min}:${sec}`;
    }
    
    return `${year}-${month}-${day}T${time}`;
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const content = event.target?.result as string;
      setPendingContent(content);
      setPendingSource(`File: ${file.name}`);
      setIsRangeModalOpen(true);
    };
    reader.readAsText(file);
  };

  const handleUrlImport = async () => {
    if (!icsUrl) {
      toast.error('Please enter a valid iCal URL');
      return;
    }
    
    setIsImporting(true);
    try {
      // Use the local proxy to bypass CORS restrictions
      const proxyUrl = `/api/proxy-ics?url=${encodeURIComponent(icsUrl)}`;
      const response = await fetch(proxyUrl);
      
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Subspace relay error: ${response.statusText}`);
      }

      const content = await response.text();
      setPendingContent(content);
      setPendingSource(icsUrl);
      setIsRangeModalOpen(true);
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || 'Transmission failed. Subspace relay could not establish a stable uplink.');
    } finally {
      setIsImporting(false);
    }
  };

  const processImport = async (content: string, source: string) => {
    setIsImporting(true);
    try {
      const allEventsData = parseIcs(content);
      
      if (allEventsData.length === 0) {
        toast.error('No valid mission data found in buffer contents');
        return;
      }

      // Filter by selected range
      const rangeStart = parseISO(startDateSync);
      const rangeEnd = parseISO(endDateSync);
      
      let eventsData = allEventsData.filter(e => {
        const eventDate = parseISO(e.startDate);
        return isWithinInterval(eventDate, { start: rangeStart, end: rangeEnd });
      });

      // After the initial sync, restrict external URL imports to events from today and in the future
      if (lastSync && source.startsWith('http')) {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        eventsData = eventsData.filter(e => {
          const eventDate = parseISO(e.startDate);
          return eventDate >= todayStart;
        });
      }

      if (eventsData.length === 0) {
        toast.info(`No missions found within the specified stardates (${startDateSync} to ${endDateSync}) or after today's threshold.`);
        return;
      }

      // 1. Fetch existing events to check for duplicates
      // We'll check for matches on name AND startDate (case-insensitive & trimmed)
      const existingEventsSnap = await getDocs(collection(db, 'events'));
      const existingMap = new Set(
        existingEventsSnap.docs.map(d => {
          const data = d.data();
          const name = (data.name || '').trim().toLowerCase();
          const startDate = (data.startDate || '').trim();
          return `${name}_${startDate}`;
        })
      );

      const eventsToImport = eventsData.filter(e => {
        const name = (e.name || '').trim().toLowerCase();
        const startDate = (e.startDate || '').trim();
        return !existingMap.has(`${name}_${startDate}`);
      });
      const duplicateCount = eventsData.length - eventsToImport.length;

      if (eventsToImport.length === 0 && duplicateCount > 0) {
        toast.info('Synchronization complete. No new missions found (all records already in database).');
        return;
      }

      let successCount = 0;
      for (const event of eventsToImport) {
        await addDoc(collection(db, 'events'), {
          name: event.name,
          startDate: event.startDate,
          endDate: event.endDate || null,
          location: event.location || '',
          type: defaultType,
          points: defaultPoints,
          status: 'active',
          description: event.description || ''
        });
        successCount++;
      }
      
      const syncInfo = {
        source,
        timestamp: new Date().toISOString(),
        count: successCount,
        startDate: startDateSync,
        endDate: endDateSync
      };

      await setDoc(doc(db, 'settings', 'calendarSyncInfo'), syncInfo);
      setLastSync(syncInfo);
      setIsRangeModalOpen(false);

      if (successCount > 0) {
        toast.success(`Success: ${successCount} new missions extracted. ${duplicateCount} duplicates ignored.`);
      } else {
        toast.info('Computer core analysis complete. Data stream verified, no new entries required.');
      }
      
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'events');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="bg-[var(--background)] border-l-8 border-lcars-blue pl-4 lg:pl-6 py-2 space-y-6">
      <div className="flex justify-between items-center">
        <span className="text-xl font-bold text-lcars-blue uppercase">Subspace Frequency Sync (Calendar Import)</span>
        <div className="p-2 bg-lcars-blue/10 rounded border border-lcars-blue/20">
          <Info className="w-4 h-4 text-lcars-blue" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Sync Status Report */}
        {lastSync && (
          <div className="md:col-span-2 p-4 bg-lcars-tan/5 border border-lcars-tan/30 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-lcars-tan/20 rounded-full">
                <History className="w-5 h-5 text-lcars-tan" />
              </div>
              <div>
                <p className="text-[0.6rem] text-lcars-tan font-bold uppercase tracking-widest">Temporal Synchronization Status</p>
                <p className="text-sm font-bold text-[var(--foreground)]">
                  Last sync performed from the <span className="text-lcars-tan">{lastSync.source.replace('File: ', '').replace('https://', '').split('/')[0]}</span>
                  {lastSync.startDate && lastSync.endDate && !isNaN(parseISO(lastSync.startDate).getTime()) && !isNaN(parseISO(lastSync.endDate).getTime()) && (
                    <> for stardates <span className="text-lcars-tan">{format(parseISO(lastSync.startDate), 'M/d/yyyy')}-{format(parseISO(lastSync.endDate), 'M/d/yyyy')}</span></>
                  )}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[0.6rem] text-[var(--foreground)]/40 font-bold uppercase tracking-widest">Submission Timestamp</p>
              <p className="text-xs font-mono text-lcars-tan">
                {lastSync.timestamp && !isNaN(new Date(lastSync.timestamp).getTime()) ? new Date(lastSync.timestamp).toLocaleString() : 'Unknown'} ({lastSync.count} New Records)
              </p>
            </div>
          </div>
        )}

        {/* Import Settings */}
        <div className="md:col-span-2 p-4 bg-lcars-blue/5 border border-lcars-blue/20 rounded-lg flex flex-wrap gap-8 items-end">
          <div className="space-y-2">
            <label htmlFor="default-type" className="text-[0.6rem] text-lcars-blue font-bold uppercase">Default Mission Type</label>
            <Select value={defaultType} onValueChange={setDefaultType}>
              <SelectTrigger id="default-type" className="w-48 bg-[var(--background)] border-lcars-blue text-[var(--foreground)] h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--background)] border-lcars-blue text-[var(--foreground)]">
                {EVENT_TYPES.filter(t => !['Department Report', 'Monthly Report'].includes(t)).map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label htmlFor="default-points" className="text-[0.6rem] text-lcars-blue font-bold uppercase">Default Points</label>
            <Input 
              id="default-points"
              type="number" 
              value={defaultPoints} 
              onChange={e => setDefaultPoints(parseInt(e.target.value))} 
              className="w-32 bg-[var(--background)] border-lcars-blue text-[var(--foreground)] h-10"
            />
          </div>
          <div className="flex-1 text-[0.6rem] text-[var(--foreground)]/40 italic pb-2">
            These parameters will be applied to all missions imported in the current batch.
          </div>
        </div>

        {/* URL Sync */}
        <div className="p-6 bg-white/5 rounded-r-[40px] border-l-4 border-lcars-blue space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <Globe className="w-5 h-5 text-lcars-blue" />
            <h3 className="font-bold text-lg tracking-tight uppercase">External Subspace Link</h3>
          </div>
          <p className="text-sm text-[var(--foreground)]/60 leading-tight">
            Synchronize using a public iCal/Google Calendar link. Note: Remote servers must allow subspace access (CORS).
          </p>
          <div className="space-y-4 pt-2">
            <Input 
              placeholder="https://example.com/calendar.ics" 
              value={icsUrl}
              onChange={e => setIcsUrl(e.target.value)}
              className="bg-[var(--background)] border-border-color focus:border-lcars-blue"
            />
            <button 
              onClick={handleUrlImport}
              disabled={isImporting || !icsUrl}
              className={`lcars-pill bg-lcars-blue text-black w-full py-2.5 text-sm font-bold flex items-center justify-center gap-2 ${isImporting ? 'opacity-50' : ''}`}
            >
              {isImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
              {isImporting ? 'SYNCHRONIZING...' : 'SYNC SUBSPACE CALENDAR'}
            </button>
          </div>
        </div>

        {/* File Upload */}
        <div className="p-6 bg-white/5 rounded-r-[40px] border-l-4 border-lcars-purple space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="w-5 h-5 text-lcars-purple" />
            <h3 className="font-bold text-lg tracking-tight uppercase">Manual Data Buffer Upload</h3>
          </div>
          <p className="text-sm text-[var(--foreground)]/60 leading-tight">
            Upload an exported .ics file from your local console (Outlook, Apple Calendar, Google).
          </p>
          <div className="pt-2">
            <input 
              type="file" 
              accept=".ics" 
              className="hidden" 
              ref={fileInputRef}
              onChange={handleFileUpload}
            />
            <button 
              onClick={() => fileInputRef.current?.click()}
              disabled={isImporting}
              className={`lcars-pill bg-lcars-purple text-black w-full py-2.5 text-sm font-bold flex items-center justify-center gap-2 ${isImporting ? 'opacity-50' : ''}`}
            >
              <Upload className="w-4 h-4" />
              {isImporting ? 'PROCESSING BUFFER...' : 'UPLOAD .ICS DATA FILE'}
            </button>
          </div>
        </div>
      </div>

      <div className="pt-4 border-t border-white/10 space-y-2">
        <div className="flex items-center gap-2 text-[0.65rem] text-[var(--foreground)]/40 font-bold tracking-widest uppercase">
          <CheckCircle2 className="w-3 h-3 text-lcars-tan" />
          Supports standard iCalendar (.ics) format
        </div>
        <div className="bg-lcars-gold/5 p-4 rounded border border-lcars-gold/20">
          <p className="text-[0.7rem] text-lcars-gold font-bold uppercase mb-2 flex items-center gap-2">
            <Globe className="w-4 h-4" /> Google Calendar Integration Steps:
          </p>
          <ol className="text-[0.65rem] text-[var(--foreground)]/60 space-y-1 ml-4 list-decimal">
            <li>Open Google Calendar and go to <span className="text-[var(--foreground)]">Settings</span>.</li>
            <li>Select the desired calendar on the left.</li>
            <li>Scroll down to <span className="text-[var(--foreground)]">Integrate calendar</span>.</li>
            <li>Copy the <span className="text-lcars-gold">Secret address in iCal format</span> URL.</li>
            <li>Paste it above and engage the sync subspace calendar button</li>
          </ol>
        </div>
      </div>

      {/* Sync Range Modal */}
      <Dialog open={isRangeModalOpen} onOpenChange={setIsRangeModalOpen}>
        <DialogContent className="bg-[var(--background)] border-2 border-lcars-blue text-[var(--foreground)] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lcars-blue flex items-center gap-2 uppercase tracking-tighter text-xl">
              <CalendarDays className="w-6 h-6" />
              Configure Sync Temporal Range
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="p-3 bg-lcars-blue/10 border border-lcars-blue/30 rounded-lg">
              <p className="text-xs text-lcars-blue font-bold uppercase mb-2">Sync Source Detected</p>
              <p className="text-sm font-mono truncate">{pendingSource}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-date-sync" className="text-[0.6rem] text-[var(--foreground)]/60 uppercase font-bold tracking-widest">Start Stardate</Label>
                <Input 
                  id="start-date-sync"
                  type="date" 
                  value={startDateSync} 
                  onChange={e => setStartDateSync(e.target.value)}
                  className="bg-[var(--background)] border-lcars-blue focus:ring-lcars-blue"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date-sync" className="text-[0.6rem] text-[var(--foreground)]/60 uppercase font-bold tracking-widest">End Stardate</Label>
                <Input 
                  id="end-date-sync"
                  type="date" 
                  value={endDateSync} 
                  onChange={e => setEndDateSync(e.target.value)}
                  className="bg-[var(--background)] border-lcars-blue focus:ring-lcars-blue"
                />
              </div>
            </div>

            <p className="text-[0.7rem] text-[var(--foreground)]/40 italic leading-relaxed">
              Only missions occurring within this temporal window will be finalized into the ship's computer. 
              Existing missions outside this range will be ignored.
            </p>
          </div>
          <DialogFooter className="bg-transparent border-none p-0 flex gap-2">
            <button 
              onClick={() => setIsRangeModalOpen(false)}
              className="flex-1 lcars-pill bg-lcars-tan text-black py-2 font-bold text-xs"
            >
              ABORT MISSION
            </button>
            <button 
              onClick={() => processImport(pendingContent, pendingSource)}
              disabled={isImporting}
              className="flex-3 lcars-pill bg-lcars-blue text-black py-2 font-bold text-xs flex items-center justify-center gap-2"
            >
              {isImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
              {isImporting ? 'SYNCHRONIZING...' : 'ENGAGE SYNC'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
